/**
 * Altas nuevas — bandeja y revisión del onboarding nuevo (repo Cucuru-com/onboarding).
 *
 * /onboarding        bandeja por estado
 * /onboarding/:id    detalle: datos, CBU verificado, documentos (sólo se bajan si
 *                    GuardDuty los dio limpios), contrato aceptado, historial y
 *                    acciones (aprobar / pedir cambios / rechazar).
 *
 * Aprobar un alta AR la pasa a "Creando collector" y responde en el acto; el
 * collector lo crea aparte la Lambda onboarding-provision (cola SQS). El
 * detalle consulta el estado cada pocos segundos mientras siga en provisioning.
 * MX queda aprobado para el alta manual.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Link, Paper, Stack, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  listOnboardings, getOnboarding, approveOnboarding, requestChanges, rejectOnboarding,
  suggestedCollectorName, DOC_LABELS, STATUS_LABELS, ObError,
  type ObDetail, type ObListItem, type ObStatus,
} from "../services/onboardingV2";

const POLL_MS = 3000;
const fmt = (iso?: string) => (iso ? new Date(iso).toLocaleString("es-AR") : "—");
const errMsg = (e: unknown) => (e instanceof ObError ? `${e.message}${e.details ? ` · ${JSON.stringify(e.details)}` : ""}` : String((e as Error)?.message ?? e));

function StatusPill({ s }: { s: ObStatus }) {
  const color = s === "submitted" ? "warning" : s === "provisioned" || s === "approved" ? "success" : s === "rejected" ? "error" : "default";
  return <Chip size="small" color={color} label={STATUS_LABELS[s]} />;
}

export default function OnboardingV2() {
  const { id } = useParams();
  return id ? <Detail id={id} /> : <Inbox />;
}

// ─── Bandeja ─────────────────────────────────────────────────────────────────
function Inbox() {
  const nav = useNavigate();
  const [status, setStatus] = useState<ObStatus>("submitted");
  const [rows, setRows] = useState<ObListItem[] | null>(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    setRows(null); setErr("");
    listOnboardings(status).then(setRows).catch(e => { setErr(errMsg(e)); setRows([]); });
  }, [status]);
  useEffect(load, [load]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h5">Altas nuevas</Typography>
        <Button startIcon={<RefreshIcon />} onClick={load}>Actualizar</Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Altas que llegan por onboarding.cucuru.com. Las del flujo viejo siguen en la pestaña Onboarding de cada collector.
      </Typography>
      <Tabs value={status} onChange={(_, v) => setStatus(v)} sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
        {(["submitted", "provisioning", "provisioned", "approved", "rejected", "draft"] as ObStatus[]).map(s =>
          <Tab key={s} value={s} label={STATUS_LABELS[s]} />)}
      </Tabs>
      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {rows === null ? <CircularProgress /> : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nombre comercial</TableCell><TableCell>CUIT / RFC</TableCell><TableCell>Tipo</TableCell>
                <TableCell>País</TableCell><TableCell>Email</TableCell><TableCell>Enviada</TableCell><TableCell>Collector</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={7}>No hay altas en este estado.</TableCell></TableRow>}
              {rows.map(r => (
                <TableRow key={r.id} hover sx={{ cursor: "pointer" }} onClick={() => nav(`/onboarding/${r.id}`)}>
                  <TableCell>{r.doing_business_as ?? "—"}</TableCell>
                  <TableCell>{r.tax_id ?? "—"}</TableCell>
                  <TableCell>{r.tax_category?.toUpperCase() ?? "—"}</TableCell>
                  <TableCell>{r.country.toUpperCase()}</TableCell>
                  <TableCell>{r.email}</TableCell>
                  <TableCell>{fmt(r.submitted_at)}</TableCell>
                  <TableCell>{r.collector_id ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}

// ─── Detalle ─────────────────────────────────────────────────────────────────
const FIELD_LABELS: [string, string][] = [
  ["tax_category", "Tipo de persona"], ["name", "Nombre / razón social"], ["surname", "Apellido"], ["tax_id", "CUIT / RFC"],
  ["doing_business_as", "Nombre comercial"], ["activity", "Rubro"], ["activity_desc", "Actividad"],
  ["telephone_number", "Teléfono"], ["social_network", "Web / red social"], ["is_aoypf", "Socio AOYPF"],
  ["bank_account", "CBU / CLABE"],
  ["addr_street", "Calle"], ["addr_number", "Número"], ["addr_floor", "Piso"], ["addr_office", "Oficina"],
  ["addr_city", "Localidad"], ["addr_state", "Provincia"], ["addr_postal_code", "CP"],
  ["rep_name", "Representante: nombre"], ["rep_surname", "Representante: apellido"], ["rep_id", "Representante: DNI"], ["rep_title", "Representante: cargo"],
];

function Detail({ id }: { id: string }) {
  const nav = useNavigate();
  const [ob, setOb] = useState<ObDetail | null>(null);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<null | "approve" | "changes" | "reject">(null);
  const [collectorName, setCollectorName] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(() => {
    setErr("");
    getOnboarding(id).then(o => { setOb(o); setCollectorName(suggestedCollectorName(String(o.data.doing_business_as ?? ""))); })
      .catch(e => setErr(errMsg(e)));
  }, [id]);
  useEffect(load, [load]);

  // Mientras se crea el collector, consultar el estado. Corta solo cuando sale
  // de provisioning (provisioned, o submitted con el error).
  const provisioning = ob?.status === "provisioning";
  useEffect(() => {
    if (!provisioning) return;
    const t = setInterval(() => {
      getOnboarding(id).then(o => {
        setOb(o);
        if (o.status === "provisioned") setOk(`Collector ${o.collector_id} creado.`);
        else if (o.status !== "provisioning") setOk("");
      }).catch(() => undefined);
    }, POLL_MS);
    return () => clearInterval(t);
  }, [provisioning, id]);

  const act = async () => {
    if (!ob || !dialog) return;
    setBusy(true); setErr(""); setOk("");
    try {
      if (dialog === "approve") {
        const r = await approveOnboarding(ob.id, ob.country === "ar" ? collectorName.trim() || undefined : undefined);
        setOk(r.status === "provisioning" ? "Alta aprobada. Creando el collector…" : "Alta aprobada.");
      } else if (dialog === "changes") {
        await requestChanges(ob.id, note.trim()); setOk("Se le pidió al comercio que corrija el alta.");
      } else {
        await rejectOnboarding(ob.id, note.trim()); setOk("Alta rechazada.");
      }
      setDialog(null); setNote(""); load();
    } catch (e) { setErr(errMsg(e)); setDialog(null); load(); }
    finally { setBusy(false); }
  };

  if (!ob) return err ? <Alert severity="error">{err}</Alert> : <CircularProgress />;
  const dirtyDocs = ob.documents.some(d => d.scan !== "NO_THREATS_FOUND");
  const canDecide = ob.status === "submitted";

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => nav("/onboarding")}>Altas nuevas</Button>
        <Typography variant="h5" sx={{ flex: 1 }}>{String(ob.data.doing_business_as ?? ob.email)}</Typography>
        <StatusPill s={ob.status} />
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 2 }}>{ok}</Alert>}
      {ob.provisioning_error && <Alert severity="error" sx={{ mb: 2 }}>Último intento de crear el collector falló: {ob.provisioning_error}</Alert>}
      {ob.status === "provisioning" && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2 }}>
          Creando el collector, suele tardar unos segundos. Si pasan más de 2 minutos, ver "Alta trabada" en docs/operacion.md del repo onboarding.
        </Alert>
      )}
      {ob.review_note && <Alert severity="info" sx={{ mb: 2 }}>Nota de revisión ({ob.reviewed_by}): {ob.review_note}</Alert>}

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="flex-start">
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle1" gutterBottom>Datos</Typography>
          <Table size="small"><TableBody>
            <TableRow><TableCell>Email (verificado)</TableCell><TableCell>{ob.email}</TableCell></TableRow>
            <TableRow><TableCell>País</TableCell><TableCell>{ob.country.toUpperCase()}</TableCell></TableRow>
            {FIELD_LABELS.filter(([k]) => ob.data[k] !== undefined && ob.data[k] !== "").map(([k, label]) => (
              <TableRow key={k}><TableCell>{label}</TableCell><TableCell>{String(ob.data[k])}</TableCell></TableRow>
            ))}
            <TableRow>
              <TableCell>Titular del CBU (BIND)</TableCell>
              <TableCell>{ob.bank_owner ? `${ob.bank_owner.name} · ${ob.bank_owner.id} · ${fmt(ob.bank_owner.checked_at)}` : ob.country === "ar" ? "Sin verificar" : "No aplica"}</TableCell>
            </TableRow>
          </TableBody></Table>
        </Paper>

        <Stack spacing={2} sx={{ flex: 1, width: "100%" }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Documentos</Typography>
            {!ob.malware_scan && <Alert severity="warning" sx={{ mb: 1 }}>Escaneo de malware desactivado en este entorno.</Alert>}
            <Table size="small"><TableBody>
              {ob.documents.map(d => (
                <TableRow key={d.type}>
                  <TableCell>{DOC_LABELS[d.type]}</TableCell>
                  <TableCell><Chip size="small" color={d.scan === "NO_THREATS_FOUND" ? "success" : d.scan === "PENDING" ? "default" : "error"} label={d.scan === "NO_THREATS_FOUND" ? "Limpio" : d.scan === "PENDING" ? "Escaneando" : d.scan} /></TableCell>
                  <TableCell>{d.download_url ? <Link href={d.download_url} rel="noreferrer">Descargar</Link> : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody></Table>
          </Paper>

          {ob.contract && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1">Contrato {ob.contract.version}</Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>Aceptado {fmt(ob.contract.accepted_at)}</Typography>
              <Box component="iframe" title="contrato" sandbox="" srcDoc={ob.contract.html}
                sx={{ width: "100%", height: 320, border: 1, borderColor: "divider", borderRadius: 1 }} />
            </Paper>
          )}

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Historial</Typography>
            {ob.history.slice().reverse().map((h, i) => (
              <Typography key={i} variant="body2">{fmt(h.at)} · {h.by} · {h.action}{h.note ? ` · ${h.note}` : ""}</Typography>
            ))}
          </Paper>
        </Stack>
      </Stack>

      {canDecide && (
        <>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={2}>
            <Button variant="contained" color="success" disabled={busy || dirtyDocs} onClick={() => setDialog("approve")}>
              {ob.country === "ar" ? "Aprobar y crear collector" : "Aprobar"}
            </Button>
            <Button variant="outlined" disabled={busy} onClick={() => setDialog("changes")}>Pedir cambios</Button>
            <Button variant="outlined" color="error" disabled={busy} onClick={() => setDialog("reject")}>Rechazar</Button>
          </Stack>
          {dirtyDocs && <Typography variant="body2" color="error" sx={{ mt: 1 }}>No se puede aprobar hasta que todos los documentos den limpio.</Typography>}
        </>
      )}

      <Dialog open={dialog !== null} onClose={() => !busy && setDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>{dialog === "approve" ? "Aprobar alta" : dialog === "changes" ? "Pedir cambios al comercio" : "Rechazar alta"}</DialogTitle>
        <DialogContent>
          {dialog === "approve" ? (ob.country === "ar" ? (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Se crea el collector activo, con liquidación a la cuenta verificada, el vendor y el usuario administrador {ob.email}. El comercio recibe un mail para entrar al portal.
              </Typography>
              <TextField label="Nombre del collector" fullWidth value={collectorName} onChange={e => setCollectorName(e.target.value)}
                helperText="Letras, números, - y _. Si existe, se agrega un número." />
            </>
          ) : <Typography variant="body2">MX no se crea solo: queda aprobado para el alta manual.</Typography>) : (
            <TextField label={dialog === "changes" ? "Qué tiene que corregir" : "Motivo"} fullWidth multiline minRows={3}
              value={note} onChange={e => setNote(e.target.value)} helperText="Lo ve el comercio." sx={{ mt: 1 }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={busy}>Cancelar</Button>
          <Button variant="contained" onClick={act} disabled={busy || (dialog !== "approve" && note.trim().length < 3)}>
            {busy ? <CircularProgress size={20} /> : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
